/**
 * Demo Mode Masking Utilities
 *
 * These functions mask sensitive data for demo presentations.
 * All masking uses block characters (█) for a consistent redacted appearance.
 */

// Allowed domains that should NOT be masked
const ALLOWED_DOMAINS = ['mediagarcia.com', 'mediagarcia.co'];

/**
 * Creates a block string of specified length
 */
function blocks(length: number): string {
  return '█'.repeat(length);
}

/**
 * Masks a string to block characters, preserving approximate length
 */
function maskString(str: string, minLength = 4): string {
  if (!str) return blocks(minLength);
  // Preserve spaces to maintain word structure
  return str.replace(/\S+/g, (word) => blocks(Math.max(word.length, 3)));
}

/**
 * Mask currency values, preserving format (K/M/B suffixes, decimal structure)
 * @param value - The currency value (number or string like "$37.1K")
 * @param showDecimals - Whether to show decimal places (default: false for compact display)
 */
export function maskCurrency(value: number | string | null | undefined, showDecimals = false): string {
  if (value === null || value === undefined) return '$XX';

  const strValue = String(value);

  // Detect suffix (K, M, B)
  const suffixMatch = strValue.match(/[KMB]$/i);
  const suffix = suffixMatch ? suffixMatch[0].toUpperCase() : '';

  // Detect if original has decimal
  const hasDecimalInOriginal = strValue.includes('.');

  // Detect approximate magnitude from the original string
  const cleanNum = strValue.replace(/[^0-9.]/g, '');
  const numValue = parseFloat(cleanNum) || 0;

  if (suffix) {
    // Has K/M/B suffix - preserve format
    if (hasDecimalInOriginal && showDecimals) {
      return `$XX.X${suffix}`;
    }
    return `$XX${suffix}`;
  }

  // No suffix - determine format based on magnitude
  if (numValue >= 1000000) {
    return showDecimals ? '$X.XM' : '$XM';
  }
  if (numValue >= 1000) {
    return showDecimals ? '$XX.XK' : '$XXK';
  }
  if (numValue >= 100) {
    return showDecimals ? '$XXX.XX' : '$XXX';
  }

  // Small amounts
  return showDecimals ? '$XX.XX' : '$XX';
}

/**
 * Mask a person's name
 * Preserves word structure (first name, last name spacing)
 */
export function maskName(name: string | null | undefined): string {
  if (!name) return blocks(8);
  return maskString(name);
}

/**
 * Mask a client/company name
 */
export function maskClientName(name: string | null | undefined): string {
  if (!name) return blocks(10);
  return maskString(name);
}

/**
 * Mask a project name
 */
export function maskProjectName(name: string | null | undefined): string {
  if (!name) return blocks(12);
  return maskString(name);
}

/**
 * Mask an email address
 * Preserves emails from allowed domains (mediagarcia.com)
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return `${blocks(6)}@${blocks(6)}.com`;

  // Check if email is from an allowed domain
  const emailLower = email.toLowerCase();
  for (const domain of ALLOWED_DOMAINS) {
    if (emailLower.endsWith(`@${domain}`)) {
      return email; // Don't mask allowed domains
    }
  }

  // Mask the email: preserve @ and domain structure
  const atIndex = email.indexOf('@');
  if (atIndex === -1) return blocks(email.length);

  const localPart = email.substring(0, atIndex);
  const domainPart = email.substring(atIndex + 1);
  const dotIndex = domainPart.lastIndexOf('.');

  if (dotIndex === -1) {
    return `${blocks(localPart.length)}@${blocks(domainPart.length)}`;
  }

  const domainName = domainPart.substring(0, dotIndex);
  const tld = domainPart.substring(dotIndex); // includes the dot

  return `${blocks(localPart.length)}@${blocks(domainName.length)}${tld}`;
}

/**
 * Mask a URL/website address
 * Preserves URLs from allowed domains (mediagarcia.com)
 */
export function maskUrl(url: string | null | undefined): string {
  if (!url) return `${blocks(8)}.com`;

  // Check if URL contains an allowed domain
  const urlLower = url.toLowerCase();
  for (const domain of ALLOWED_DOMAINS) {
    if (urlLower.includes(domain)) {
      return url; // Don't mask allowed domains
    }
  }

  // Try to preserve URL structure
  try {
    // Handle URLs with or without protocol
    const hasProtocol = url.includes('://');
    const urlToParse = hasProtocol ? url : `https://${url}`;
    const parsed = new URL(urlToParse);

    const maskedHost = parsed.hostname
      .split('.')
      .map((part, index, arr) => {
        // Keep TLD visible
        if (index === arr.length - 1) return part;
        return blocks(part.length);
      })
      .join('.');

    const protocol = hasProtocol ? `${parsed.protocol}//` : '';
    const path = parsed.pathname !== '/' ? `/${blocks(4)}` : '';

    return `${protocol}${maskedHost}${path}`;
  } catch {
    // If URL parsing fails, just mask the whole thing
    return blocks(url.length);
  }
}

/**
 * Mask hours/time values
 * Converts to XX.X hrs format
 */
export function maskHours(hours: number | string | null | undefined): string {
  if (hours === null || hours === undefined) return 'XX.X hrs';

  // Check if the value has decimals
  const numValue = typeof hours === 'string' ? parseFloat(hours) : hours;
  const hasDecimals = numValue % 1 !== 0;

  return hasDecimals ? 'XX.X hrs' : 'XX hrs';
}

/**
 * Mask a phone number
 * Preserves structure but replaces digits
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '(XXX) XXX-XXXX';

  // Replace all digits with X, preserve formatting characters
  return phone.replace(/\d/g, 'X');
}

/**
 * Mask a physical address
 */
export function maskAddress(address: string | null | undefined): string {
  if (!address) return blocks(20);
  return maskString(address);
}

/**
 * Soften warning/alert text
 * Converts harsh language to gentler alternatives
 */
export function softenAlertText(text: string | null | undefined): string {
  if (!text) return '';

  const replacements: Record<string, string> = {
    // Uppercase versions
    'OVERDUE': 'Needs attention',
    'LATE': 'Pending',
    'URGENT': 'Priority',
    'CRITICAL': 'Important',
    'FAILED': 'Incomplete',
    'ERROR': 'Issue',
    'WARNING': 'Notice',
    'DANGER': 'Attention',
    'EXPIRED': 'Ended',
    'PAST DUE': 'Follow up',
    'AT RISK': 'Monitor',
    'BLOCKED': 'On hold',
    // Lowercase versions
    'overdue': 'needs attention',
    'late': 'pending',
    'urgent': 'priority',
    'critical': 'important',
    'failed': 'incomplete',
    'error': 'issue',
    'warning': 'notice',
    'danger': 'attention',
    'expired': 'ended',
    'past due': 'follow up',
    'at risk': 'monitor',
    'blocked': 'on hold',
  };

  let result = text;
  for (const [pattern, replacement] of Object.entries(replacements)) {
    result = result.replace(new RegExp(pattern, 'g'), replacement);
  }

  return result;
}

/**
 * Type definitions for mask types
 */
export type MaskType =
  | 'currency'
  | 'name'
  | 'clientName'
  | 'projectName'
  | 'email'
  | 'url'
  | 'hours'
  | 'phone'
  | 'address'
  | 'alertText';

/**
 * Generic mask function that routes to the appropriate masker
 */
export function mask(
  value: string | number | null | undefined,
  type: MaskType,
  options?: { showDecimals?: boolean }
): string {
  switch (type) {
    case 'currency':
      return maskCurrency(value as number | string, options?.showDecimals);
    case 'name':
      return maskName(value as string);
    case 'clientName':
      return maskClientName(value as string);
    case 'projectName':
      return maskProjectName(value as string);
    case 'email':
      return maskEmail(value as string);
    case 'url':
      return maskUrl(value as string);
    case 'hours':
      return maskHours(value as number | string);
    case 'phone':
      return maskPhone(value as string);
    case 'address':
      return maskAddress(value as string);
    case 'alertText':
      return softenAlertText(value as string);
    default:
      return String(value ?? '');
  }
}
